import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { createLocalAudio, SPOKEN_TEXT_ATTRS } from '../lib/speechPlayback';
import {
  CLIENT_KEY_STORAGE,
  MENTEE_KEY_STORAGE,
  REGISTRATION_PATHWAY_STORAGE,
  SCREENS,
  resolveGrandFoyerRoute,
} from '../lib/screenRegistry';
import './Introduction.css';

const ASSETS = {
  audio: encodeURI("/Assets/Founder/welcome Andre's mentor.wav"),
  script: encodeURI('/Assets/Founder/Welcome Andre Mento.txt'),
  logo: encodeURI('/Assets/logo/Mentology Logo.png'),
};

const HANDOVER_SCRIPT =
  "Welcome. I am your AI Mentor. Together, we will study and implement the mentoring relationship through the Mentology framework. We begin with your Setup: The Success Guide. To mentor you effectively, I need to know your business—its location, your team size, and your core mission. These details are your 'Success Seeds.' They allow me to anchor my Socratic inquiry into the specific reality of your SMB operations. Your data is secure. Whether you access me via phone, tablet, or PC, your intellectual property remains in your private, encrypted vault. Remember: I am here to mentor your logic. I will not provide shortcuts, summaries, or direct advice. I am your Socratic Auditor. I will ask the 'Why' and the 'How' to bridge your logic gaps. You provide the insight; I provide the frame. Your breakthrough starts with your first session. Unlock your insight. Let's begin.";

const WPM = 45;
const MS_PER_CHAR = ((60 / WPM) * 1000) / 5;

export default function Introduction() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scriptText, setScriptText] = useState(HANDOVER_SCRIPT);
  const [displayText, setDisplayText] = useState('');
  const [showProceed, setShowProceed] = useState(false);
  const [proceeding, setProceeding] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const charIndex = useRef(0);
  const fallbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playedScriptRef = useRef('');
  const proceedRef = useRef(false);

  const stopFounderAudio = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    audio.src = '';
    audioRef.current = null;
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetch(ASSETS.script)
      .then((res) => (res.ok ? res.text() : Promise.reject()))
      .then((text) => {
        if (cancelled) return;
        const normalized = text.trim();
        if (normalized) setScriptText(normalized);
      })
      .catch(() => {
        if (!cancelled) setScriptText(HANDOVER_SCRIPT);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      stopFounderAudio();
    };
  }, [stopFounderAudio]);

  useEffect(() => {
    if (!scriptText || playedScriptRef.current === scriptText) return;
    playedScriptRef.current = scriptText;

    charIndex.current = 0;
    setDisplayText('');
    setShowProceed(false);
    stopFounderAudio();

    const script = scriptText;
    const scriptLength = script.length;

    const revealProceedGate = () => {
      setDisplayText(script);
      setShowProceed(true);
    };

    const syncTypewriterFallback = () => {
      if (charIndex.current < scriptLength) {
        setDisplayText(script.slice(0, charIndex.current + 1));
        charIndex.current += 1;
        fallbackTimer.current = setTimeout(syncTypewriterFallback, MS_PER_CHAR);
      } else {
        revealProceedGate();
      }
    };

    const syncTypewriterToAudio = () => {
      const audio = audioRef.current;
      if (!audio || !audio.duration || !Number.isFinite(audio.duration)) return;

      const progress = Math.min(audio.currentTime / audio.duration, 1);
      const targetIndex = Math.floor(progress * scriptLength);
      charIndex.current = targetIndex;
      setDisplayText(script.slice(0, targetIndex));

      if (progress >= 1 || /Let['\u2019]s begin\.$/.test(script.slice(0, targetIndex))) {
        revealProceedGate();
      }
    };

    const audio = createLocalAudio(ASSETS.audio);
    audioRef.current = audio;
    let audioCleanup: (() => void) | undefined;

    const startSynchronizedPlayback = () => {
      const finishHandover = () => revealProceedGate();

      const startFallback = () => {
        charIndex.current = 0;
        syncTypewriterFallback();
        fallbackTimer.current = setTimeout(finishHandover, scriptLength * MS_PER_CHAR + 400);
      };

      const onTimeUpdate = () => syncTypewriterToAudio();
      const onEnded = () => finishHandover();

      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('ended', onEnded);

      audio.play().catch(() => {
        startFallback();
      });

      audioCleanup = () => {
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.removeEventListener('ended', onEnded);
      };
    };

    if (audio.readyState >= 1) {
      startSynchronizedPlayback();
    } else {
      const onReady = () => startSynchronizedPlayback();
      audio.addEventListener('loadedmetadata', onReady, { once: true });
      audioCleanup = () => audio.removeEventListener('loadedmetadata', onReady);
    }

    return () => {
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
      audioCleanup?.();
      stopFounderAudio();
    };
  }, [scriptText, stopFounderAudio]);

  const getProceedConfig = useCallback(() => {
    const registrationPathway = sessionStorage.getItem(REGISTRATION_PATHWAY_STORAGE);
    const storedKey =
      sessionStorage.getItem(MENTEE_KEY_STORAGE)
      || sessionStorage.getItem(CLIENT_KEY_STORAGE)
      || user?.userToken
      || '';

    const foyerRoute = resolveGrandFoyerRoute(storedKey, registrationPathway);
    if (foyerRoute) return foyerRoute;

    if (!user) return { label: '[ Continue ]', route: '/portal-login' };
    switch (user.role) {
      case 'PRO':
        return { label: 'Enter Briefing Room', route: '/pro-setup' };
      case 'ELT':
        return { label: 'Enter Briefing Room', route: '/workspace' };
      case 'ORG':
        return { label: '[ Enter Admin Console ]', route: '/org-admin' };
      default:
        return { label: '[ Continue ]', route: '/workspace' };
    }
  }, [user]);

  const handleProceed = async () => {
    if (proceeding || proceedRef.current) return;
    proceedRef.current = true;
    setProceeding(true);
    stopFounderAudio();
    try {
      if (user) {
        await authApi.completeHandover();
      }
      const { route } = getProceedConfig();
      navigate(route, { replace: true });
    } finally {
      setProceeding(false);
    }
  };

  const proceedConfig = getProceedConfig();

  return (
    <div className="introduction-cinematic" data-screen-id={SCREENS.ELT05}>
      <div className="introduction-stage">
        <div className="introduction-brand-stack">
          <img
            src={ASSETS.logo}
            alt="Mentology"
            className="introduction-logo brand-logo"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />

          <div className={`introduction-text-box ${SPOKEN_TEXT_ATTRS.className}`}>
            <div className={`introduction-typewriter-body scannable-bullet-text ${SPOKEN_TEXT_ATTRS.className}`}>
              {displayText}
              {!showProceed && displayText.length < scriptText.length && (
                <span className="introduction-cursor">|</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`introduction-gate ${showProceed ? 'introduction-gate-visible' : ''}`}>
        <button
          type="button"
          className="btn-primary introduction-proceed-btn"
          onClick={handleProceed}
          disabled={proceeding}
          aria-hidden={!showProceed}
          tabIndex={showProceed ? 0 : -1}
        >
          {proceeding ? 'Advancing...' : proceedConfig.label}
        </button>
      </div>
    </div>
  );
}
